import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { APIService } from 'src/api/api';
import { getFormattedDate } from 'src/utils/getInitials';

const initialState = {
  mileageList: [],
  reconciliationList: [],
  otherReportList: [],
  totalCount: 0,
  nextPage: null,
  loading: false
};

export const getMileageList = createAsyncThunk(
  'reports/mileage',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getMileageData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const loadMoreMileageList = createAsyncThunk(
  'reports/mileage/loadmore',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getMileageData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getReconciliationList = createAsyncThunk(
  'reports/reconciliation',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getReconciliationData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const loadMoreReconciliationList = createAsyncThunk(
  'reports/reconciliation/loadmore',
  async (req, { rejectWithValue }) => {
    const { limit, pageNo, params } = req;
    try {
      const res = await APIService.getReconciliationData(limit, pageNo, params);
      if (!res.success) throw res;
      return res.result;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

export const getOtherReportsData = createAsyncThunk(
  'reports/other-report',
  async (req, { rejectWithValue }) => {
    const { url } = req;
    try {
      const res = await APIService.getOtherReportsData(url);
      if (!res.success) throw res;
      const data = res.result?.results || res.result;
      const count = res.result?.count || data.length;
      return { count, data, nextPage: res.result?.next || null };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

function flatObject(item) {
  const flatItem = {};
  const ItemKeys = Object.keys(item);
  ItemKeys.forEach((key) => {
    const value = item[key];
    if (typeof value !== 'object') {
      let listItem = value;
      if (key.toLowerCase().includes('time') || key.toLowerCase().includes('date')) {
        listItem = getFormattedDate(value);
      }
      flatItem[key] = listItem;
    }
  });
  return flatItem;
}

const formatOtherReportData = (data) => {
  const updatedData = data.map((item) => flatObject(item));
  return updatedData;
};

const formatReport = (data) => {
  const updatedData = data.map((item) => {
    const updatedItem = {
      ...item,
      ...item.mileage_info,
      ...item.card_info
    };
    return updatedItem;
  });
  return updatedData;
};

export const reportSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    resetOtherReportData: (state) => ({
      ...state, otherReportList: [], totalCount: 0, nextPage: null
    }),
    setShiftTimings: (state, action) => ({
      ...state, shiftTimings: action.payload
    }),
  },
  extraReducers: (builder) => {
    // add your async reducers here
    builder
      .addCase(getMileageList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        mileageList: formatReport(action.payload.results)
      }))
      .addCase(getMileageList.pending, (state) => ({ ...state, loading: true }))
      .addCase(getMileageList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreMileageList.pending, (state) => ({ ...state, loading: true }))
      .addCase(loadMoreMileageList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreMileageList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        mileageList: formatReport(action.payload.results),
      }))
      .addCase(getOtherReportsData.pending, (state) => ({ ...state, loading: true }))
      .addCase(getOtherReportsData.rejected, (state) => ({ ...state, loading: false }))
      .addCase(getOtherReportsData.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        nextPage: action.payload.nextPage,
        otherReportList: formatOtherReportData(action.payload.data),
      }))
      .addCase(getReconciliationList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        reconciliationList: action.payload.results
      }))
      .addCase(getReconciliationList.pending, (state) => ({ ...state, loading: true }))
      .addCase(getReconciliationList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreReconciliationList.pending, (state) => ({ ...state, loading: true }))
      .addCase(loadMoreReconciliationList.rejected, (state) => ({ ...state, loading: false }))
      .addCase(loadMoreReconciliationList.fulfilled, (state, action) => ({
        ...state,
        loading: false,
        totalCount: action.payload.count,
        reconciliationList: action.payload.results,
      }));
  }
});

// Action creators are generated for each case reducer function
export const {
  setShiftTimings,
  resetOtherReportData,
} = reportSlice.actions;

export default reportSlice.reducer;
